import { v4 as uuidv4 } from 'uuid';

import { putImageBlob, addImageRef } from './imageBlobStore';
import { getImageSize, fitWidthKeepAspect } from './imageUtils';

const IMG_TAG = /<img\s+([^>]*?)\s*\/?>/gi;
const SRC_DATA_URL = /src\s*=\s*"(data:image\/([a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/=]+))"/i;

function base64ToBlob(base64: string, mime: string): Blob {
    const binary = atob(base64);
    const buffer = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        buffer[i] = binary.charCodeAt(i);
    }
    return new Blob([buffer], { type: mime });
}

function normalizeExtension(type: string): string {
    const lower = type.toLowerCase();
    if (lower === 'jpeg') return 'jpg';
    return lower;
}

function setOrInsertAttr(attrs: string, name: string, value: string): string {
    const re = new RegExp(`\\b${name}\\s*=\\s*"[^"]*"`, 'i');
    if (re.test(attrs)) {
        return attrs.replace(re, `${name}="${value}"`);
    }
    return `${attrs} ${name}="${value}"`.trim();
}

function hasAttr(attrs: string, name: string): boolean {
    return new RegExp(`\\b${name}\\s*=`, 'i').test(attrs);
}

export async function convertBase64ImagesInHtml(html: string): Promise<string> {
    if (!html) return html;

    const matches: { full: string; attrs: string }[] = [];
    let m;
    IMG_TAG.lastIndex = 0;
    while ((m = IMG_TAG.exec(html)) !== null) {
        matches.push({ full: m[0], attrs: m[1] });
    }
    if (!matches.length) return html;

    const replacements = await Promise.all(
        matches.map(async ({ full, attrs }) => {
            const srcMatch = SRC_DATA_URL.exec(attrs);
            if (!srcMatch) return { full, replacement: full };

            const [srcWhole, , type, base64] = srcMatch;
            const ext = normalizeExtension(type);
            const fileName = `${uuidv4()}.${ext}`;
            const blob = base64ToBlob(base64, `image/${type.toLowerCase()}`);
            putImageBlob(fileName, blob);
            addImageRef(fileName);

            let newAttrs = attrs.replace(srcWhole, `src="local://${fileName}"`);

            if (!hasAttr(newAttrs, 'width') || !hasAttr(newAttrs, 'height')) {
                try {
                    const { width: w, height: h } = await getImageSize(blob);
                    const { width, height } = fitWidthKeepAspect(w, h, 600);
                    if (!hasAttr(newAttrs, 'width')) {
                        newAttrs = setOrInsertAttr(newAttrs, 'width', String(width));
                    }
                    if (!hasAttr(newAttrs, 'height')) {
                        newAttrs = setOrInsertAttr(newAttrs, 'height', String(height));
                    }
                } catch {
                    // best-effort: leave size attrs unset on decode failure
                }
            }

            return { full, replacement: `<img ${newAttrs}>` };
        })
    );

    let result = html;
    for (const { full, replacement } of replacements) {
        if (full === replacement) continue;
        result = result.replace(full, replacement);
    }
    return result;
}
